import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { BonificoPanel, MetodoSwitch, SumUpPanel } from "@/components/BonificoPanel";
import { IdCard, HandCoins, Wallet, CheckCircle2, Clock, Save, Download, Check, X, FileSpreadsheet, FileText, CreditCard } from "lucide-react";
import { fmtDateTime } from "@/lib/constants";
import { toast } from "sonner";

const downloadReceipt = async (id) => {
  try {
    const res = await api.get(`/payments/${id}/ricevuta`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ricevuta_${id.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Impossibile scaricare la ricevuta");
  }
};

const downloadDonazioni = async (year, format) => {
  try {
    const res = await api.get("/payments/donazioni/export", { params: { year, format }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `erogazioni_liberali_${year}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Export non riuscito");
  }
};

const DonazioniExport = () => {
  const cur = new Date().getFullYear();
  const [year, setYear] = useState(cur);
  return (
    <Card className="mb-6 border-slate-200 shadow-sm" data-testid="donazioni-export-card">
      <CardContent className="flex flex-wrap items-end gap-4 p-5">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-rose-50"><HandCoins className="h-6 w-6 text-rose-600" /></div>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-base font-bold text-slate-900">Riepilogo erogazioni liberali</h2>
          <p className="text-sm text-slate-500">Donazioni completate dell'anno con donatore, Codice Fiscale e importo, pronte per il commercialista.</p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="donazioni-year" className="text-xs">Anno</Label>
          <select id="donazioni-year" data-testid="donazioni-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="h-9 w-28 rounded-md border border-slate-300 bg-white px-2 text-sm">
            {Array.from({ length: 6 }, (_, i) => cur - i).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <Button variant="outline" onClick={() => downloadDonazioni(year, "xlsx")} data-testid="donazioni-export-excel"><FileSpreadsheet className="mr-2 h-4 w-4 text-[#166534]" /> Excel</Button>
        <Button variant="outline" onClick={() => downloadDonazioni(year, "pdf")} data-testid="donazioni-export-pdf"><FileText className="mr-2 h-4 w-4 text-red-700" /> PDF</Button>
      </CardContent>
    </Card>
  );
};

const STATUS = {
  COMPLETED: "bg-green-100 text-green-800 border-green-200",
  CREATED: "bg-amber-100 text-amber-800 border-amber-200",
  PENDING: "bg-sky-100 text-sky-800 border-sky-200",
  FAILED: "bg-red-100 text-red-800 border-red-200",
  REJECTED: "bg-red-100 text-red-800 border-red-200",
};
const STATUS_LABEL = { COMPLETED: "Completato", CREATED: "In corso", PENDING: "In attesa di conferma", FAILED: "Fallito", REJECTED: "Rifiutato" };

const metodoLabel = (t) => (t.origine === "manuale" ? "Manuale" : t.metodo === "bonifico" ? "Bonifico" : t.metodo === "sumup" ? "SumUp" : "PayPal");

const SUMUP_FIELDS = [["link_quota", "Link quota associativa (importo fisso)"], ["link_donazione", "Link donazioni (importo libero)"], ["link_altro", "Link altri pagamenti (importo libero)"]];

const SumUpLinksCard = ({ config }) => {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(null);
  const vals = draft ?? config?.sumup ?? {};
  const save = async () => {
    try {
      await api.put("/payments/sumup-links", vals);
      await qc.invalidateQueries({ queryKey: ["paypal-config"] });
      setDraft(null);
      toast.success("Link SumUp salvati");
    } catch (err) { toast.error(err.response?.data?.detail || "Salvataggio non riuscito"); }
  };
  return (
    <Card className="mb-6 border-slate-200 shadow-sm" data-testid="sumup-links-card">
      <CardContent className="space-y-4 p-5">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50"><CreditCard className="h-6 w-6 text-[#D97706]" /></div>
          <div>
            <h2 className="font-heading text-base font-bold text-slate-900">Link di pagamento SumUp</h2>
            <p className="text-sm text-slate-500">Crea i link nell'app SumUp (Link di pagamento) e incollali qui. Se un link manca, quella voce mostra solo il bonifico.</p>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {SUMUP_FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1">
              <Label className="text-xs">{l}</Label>
              <Input value={vals[k] || ""} placeholder="https://pay.sumup.com/..." data-testid={`sumup-${k}`}
                onChange={(e) => setDraft({ ...vals, [k]: e.target.value })} />
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={!draft} className="bg-[#14382C] hover:bg-[#0E281F]" data-testid="sumup-links-save"><Save className="mr-2 h-4 w-4" /> Salva link</Button>
      </CardContent>
    </Card>
  );
};

const BonificoActions = ({ t, onDone }) => {
  const act = async (what) => {
    if (what === "rifiuta" && !window.confirm("Rifiutare questo pagamento? Usalo se l'accredito non è arrivato.")) { return; }
    try {
      await api.post(`/payments/${t.id}/${what}`);
      toast.success(what === "conferma" ? "Pagamento confermato, ricevuta inviata via email se disponibile" : "Pagamento rifiutato");
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Operazione non riuscita"); }
  };
  return (
    <span className="inline-flex gap-1">
      <Button size="sm" className="h-7 bg-[#166534] px-2 hover:bg-[#14532d]" onClick={() => act("conferma")} data-testid={`confirm-${t.id}`}><Check className="mr-1 h-3.5 w-3.5" /> Conferma</Button>
      <Button size="sm" variant="outline" className="h-7 px-2 text-red-700" onClick={() => act("rifiuta")} data-testid={`reject-${t.id}`}><X className="h-3.5 w-3.5" /></Button>
    </span>
  );
};

const TIPO_LABEL = { tesseramento: "Quota associativa", donazione: "Donazione", altro: "Altro" };

function PaymentsTable({ items, showDonor, onChanged }) {
  if (!items?.length)
    return <p className="py-6 text-center text-sm text-slate-400">Nessun pagamento registrato.</p>;
  return (
    <div className="overflow-x-auto gz-scroll">
      <table className="w-full text-sm">
        <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="py-2">Data</th><th className="py-2">Tipo</th>
            {showDonor && <th className="py-2">Da</th>}
            <th className="py-2 pr-3">Causale</th><th className="py-2 pr-3">Metodo</th><th className="py-2 pr-3">Importo</th><th className="py-2 pr-3">Stato</th><th className="py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((t) => (
            <tr key={t.id} data-testid={`payment-${t.id}`}>
              <td className="py-2 text-slate-600">{fmtDateTime(t.created_at)}</td>
              <td className="py-2">{TIPO_LABEL[t.tipo] || t.tipo}</td>
              {showDonor && <td className="py-2 text-slate-600">{t.donor_name || t.donor_email || "—"}</td>}
              <td className="py-2 text-slate-600">{t.causale}</td>
              <td className="py-2 text-slate-600">{metodoLabel(t)}{t.riferimento ? <span className="block text-[11px] text-slate-400">rif. {t.riferimento}</span> : null}</td>
              <td className="py-2 pr-3 font-mono-code font-semibold">€ {t.amount.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              <td className="py-2"><Badge variant="outline" className={STATUS[t.status] || "bg-slate-100"}>{STATUS_LABEL[t.status] || t.status}</Badge></td>
              <td className="py-2 text-right">
                {showDonor && t.status === "PENDING" && ["bonifico", "sumup"].includes(t.metodo) && <BonificoActions t={t} onDone={onChanged} />}
                {t.status === "COMPLETED" && (
                  <Button variant="ghost" size="sm" className="h-7 text-[#166534] hover:bg-emerald-50" data-testid={`receipt-${t.id}`} onClick={() => downloadReceipt(t.id)}>
                    <Download className="mr-1 h-3.5 w-3.5" /> Ricevuta
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Pagamenti() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isManager = ["admin", "coordinatore"].includes(user?.role);
  const [tipo, setTipo] = useState("tesseramento");
  const [amount, setAmount] = useState(25);
  const [causale, setCausale] = useState("");
  const [metodo, setMetodo] = useState("bonifico");
  const [donorCf, setDonorCf] = useState("");

  const { data: config } = useQuery({ queryKey: ["paypal-config"], queryFn: async () => (await api.get("/payments/config")).data });
  const { data: mine = [] } = useQuery({ queryKey: ["payments-mine"], queryFn: async () => (await api.get("/payments/mine")).data });
  const { data: all } = useQuery({ queryKey: ["payments-all"], queryFn: async () => (await api.get("/payments")).data, enabled: isManager });

  const onSuccess = () => {
    qc.invalidateQueries({ queryKey: ["payments-mine"] });
    qc.invalidateQueries({ queryKey: ["payments-all"] });
  };

  const fixed = config?.tesseramento_amount ?? 25;
  const presets = config?.donazione_presets ?? [10, 25, 50, 100];
  const payAmount = tipo === "tesseramento" ? fixed : amount;
  const validAmount = tipo === "tesseramento" || (amount >= 1 && amount <= 10000);
  const nome = user?.name || "";
  const causaleBonifico = tipo === "tesseramento"
    ? `Quota associativa ${new Date().getFullYear()} - ${nome}`
    : tipo === "donazione" ? `${config?.causale_donazione || "Erogazione liberale"} - ${nome}` : `${causale || "Pagamento"} - ${nome}`;

  const [quotaDraft, setQuotaDraft] = useState("");
  const [savingQuota, setSavingQuota] = useState(false);
  const saveQuota = async () => {
    const val = parseFloat(quotaDraft);
    if (!(val >= 1 && val <= 10000)) {
      toast.error("La quota deve essere tra 1 e 10.000 EUR");
      return;
    }
    setSavingQuota(true);
    try {
      await api.put("/payments/quota-amount", { amount: val });
      await qc.invalidateQueries({ queryKey: ["paypal-config"] });
      setQuotaDraft("");
      toast.success(`Quota associativa aggiornata a € ${val.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSavingQuota(false);
    }
  };

  return (
    <div className="gz-fade-up">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Pagamenti</h1>
        <p className="mt-1 text-sm text-slate-500">Quota associativa, donazioni e altri versamenti con bonifico bancario o carta tramite SumUp (EUR).</p>
      </div>

      {isManager && all && (
        <Card className="mb-6 border-slate-200 bg-[#14382C] text-white shadow-sm">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#166534]"><Wallet className="h-6 w-6" /></div>
            <div>
              <p className="font-heading text-3xl font-extrabold">€ {all.totale_incassato.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="text-xs uppercase tracking-wide text-emerald-100/70">Totale incassato (completati)</p>
            </div>
          </CardContent>
        </Card>
      )}

      {isManager && (
        <Card className="mb-6 border-slate-200 shadow-sm" data-testid="quota-setting-card">
          <CardContent className="flex flex-wrap items-end gap-4 p-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50"><IdCard className="h-6 w-6 text-[#166534]" /></div>
            <div className="min-w-0 flex-1">
              <h2 className="font-heading text-base font-bold text-slate-900">Quota associativa annuale</h2>
              <p className="text-sm text-slate-500">Importo attuale: <span className="font-semibold text-[#14382C]">€ {fixed.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>. Modificabile dagli amministratori.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="quota-amount-input" className="text-xs">Nuovo importo (EUR)</Label>
              <Input id="quota-amount-input" type="number" min={1} max={10000} step="0.01" className="w-36"
                data-testid="quota-amount-input" placeholder={String(fixed)}
                value={quotaDraft} onChange={(e) => setQuotaDraft(e.target.value)} />
            </div>
            <Button className="bg-[#14382C] hover:bg-[#0E281F]" onClick={saveQuota} disabled={savingQuota || !quotaDraft} data-testid="save-quota-amount">
              <Save className="mr-2 h-4 w-4" /> Salva quota
            </Button>
          </CardContent>
        </Card>
      )}

      {isManager && <SumUpLinksCard config={config} />}

      {isManager && <DonazioniExport />}

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="border-slate-200 shadow-sm lg:col-span-2">
          <CardContent className="p-6">
            <h2 className="mb-4 font-heading text-lg font-bold text-slate-900">Effettua un pagamento</h2>

            <div className="mb-4 grid grid-cols-3 gap-2">
              {[
                { k: "tesseramento", label: "Quota assoc.", icon: IdCard },
                { k: "donazione", label: "Donazione", icon: HandCoins },
                { k: "altro", label: "Altro", icon: Wallet },
              ].map((o) => (
                <button
                  key={o.k}
                  data-testid={`paytype-${o.k}`}
                  onClick={() => { setTipo(o.k); if (o.k !== "tesseramento") setAmount(presets[1] || 25); }}
                  className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-xs font-medium transition-colors ${
                    tipo === o.k ? "border-[#14382C] bg-emerald-50 text-[#14382C]" : "border-slate-200 text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  <o.icon className="h-5 w-5" /> {o.label}
                </button>
              ))}
            </div>

            {tipo === "tesseramento" ? (
              <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-slate-500">Quota associativa annuale</p>
                <p className="font-heading text-3xl font-extrabold text-[#14382C]">€ {fixed.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            ) : (
              <div className="mb-4 space-y-3">
                <div className="flex flex-wrap gap-2">
                  {presets.map((p) => (
                    <button
                      key={p}
                      data-testid={`preset-${p}`}
                      onClick={() => setAmount(p)}
                      className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                        amount === p ? "border-[#14382C] bg-[#14382C] text-white" : "border-slate-300 text-slate-600 hover:border-[#14382C]"
                      }`}
                    >€ {p}</button>
                  ))}
                </div>
                <div className="space-y-2">
                  <Label>Importo personalizzato (EUR)</Label>
                  <Input type="number" min={1} max={10000} step="0.01" data-testid="payment-amount"
                    value={amount} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} />
                </div>
                {tipo === "donazione" ? (
                  <>
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="donazione-causale-info">
                      Causale: <strong>{config?.causale_donazione}</strong>. Riceverai una ricevuta valida per le agevolazioni fiscali.
                    </div>
                    <div className="space-y-2">
                      <Label>Codice Fiscale del donatore (per la detrazione)</Label>
                      <Input data-testid="payment-donor-cf" value={donorCf} maxLength={16} onChange={(e) => setDonorCf(e.target.value.toUpperCase())} />
                    </div>
                  </>
                ) : (
                <div className="space-y-2">
                  <Label>Causale (facoltativa)</Label>
                  <Input data-testid="payment-causale" value={causale} onChange={(e) => setCausale(e.target.value)}
                    placeholder="Es. Rimborso spese" />
                </div>
                )}
              </div>
            )}

            <MetodoSwitch value={metodo} onChange={setMetodo} />
            {metodo === "bonifico" ? (
              <BonificoPanel config={config} tipo={tipo} amount={payAmount} causale={causale} causaleBonifico={causaleBonifico}
                donorCf={donorCf} disabled={!validAmount} onSuccess={onSuccess} />
            ) : (
            <SumUpPanel config={config} tipo={tipo} amount={payAmount} causale={causale} causaleBonifico={causaleBonifico}
              donorCf={donorCf} disabled={!validAmount} onSuccess={onSuccess} />
            )}
          </CardContent>
        </Card>

        <div className="space-y-6 lg:col-span-3">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Clock className="h-5 w-5 text-[#166534]" /> I miei pagamenti</h2>
              <PaymentsTable items={mine} />
            </CardContent>
          </Card>

          {isManager && (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><CheckCircle2 className="h-5 w-5 text-[#166534]" /> Tutti i pagamenti</h2>
                <PaymentsTable items={all?.items} showDonor onChanged={onSuccess} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
